// CustomSnackbar.jsx
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

/**
 * A reusable MUI Snackbar wrapper.
 *
 * Props
 * -----
 * open            (bool,  required) – show / hide state (controlled)
 * onClose         (func,  required) – called when the snackbar is dismissed
 * message         (node,  required) – main text / JSX
 * severity        (string)          – 'success' | 'info' | 'warning' | 'error' (controls background)
 * color           (string)          – deprecated; snackbar text/icons are always white
 * duration        (number)          – auto-hide ms (default 4000; set null to disable)
 * vertical        (string)          – 'top' | 'bottom'  (default 'bottom')
 * horizontal      (string)          – 'left' | 'center' | 'right' (default 'center')
 * variant         (string)          – 'standard' | 'outlined' | 'filled' (default 'filled')
 * action          (node)            – optional JSX (e.g., <Button/>)
 * sx              (object)          – extra MUI `sx` styles for the Alert
 * ...snackbarProps               – forwarded to <Snackbar>
 */
const CustomSnackbar = ({
  open,
  onClose,
  message,
  severity = 'info',
  color: deprecatedColor,
  duration = 4000,
  vertical = 'bottom',
  horizontal = 'center',
  variant = 'filled',
  action,
  sx,
  ...snackbarProps
}) => {
  const { sx: snackbarSx, ...restSnackbarProps } = snackbarProps;
  const textColor = deprecatedColor ? '#fff' : '#fff';

  return (
    <Snackbar
      open={open}
      onClose={onClose}
      autoHideDuration={duration ?? null}
      anchorOrigin={{ vertical, horizontal }}
      sx={{
        zIndex: 2147483647,
        ...(snackbarSx || {}),
      }}
      {...restSnackbarProps}>
      <Alert
        onClose={onClose}
        severity={severity}
        variant={variant}
        action={action}
        sx={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiAlert-message': {
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
          },
          '& .MuiAlert-icon': {
            marginRight: 1.0,
          },
          '&& .MuiAlert-action': {
            padding: '0 !important',
            marginLeft: '1rem',
            marginRight: '0 !important',
            alignItems: 'center',
          },
          '&& .MuiAlert-action .MuiIconButton-root': {
            padding: '0 !important',
          },
          ...(sx || {}),
          color: `${textColor} !important`,
          '& .MuiAlert-message': {
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            color: `${textColor} !important`,
          },
          '& .MuiAlert-icon': {
            marginRight: 1.0,
            color: `${textColor} !important`,
          },
          '& .MuiAlert-action': { color: `${textColor} !important` },
          '& .MuiAlert-action .MuiIconButton-root': {
            color: `${textColor} !important`,
          },
        }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

export default CustomSnackbar;
